import type { SwaAuthMeResponse, SwaClientPrincipal } from '@oscom/shared';

/**
 * Static Web Apps authentication endpoints.
 *
 * The frontend never handles credentials, tokens or passwords: it only
 * redirects to `/.auth/login/aad` and reads `/.auth/me`. Everything else is
 * handled by the platform and verified again by the API.
 */

export const AUTH_ROUTES = {
  me: '/.auth/me',
  login: '/.auth/login/aad',
  logout: '/.auth/logout',
} as const;

/**
 * Only same-site absolute paths are accepted as a redirect target. Without this
 * check, `?post_login_redirect_uri=https://evil.example` would turn the login
 * flow into an open redirect.
 */
export function sanitizeReturnPath(candidate: string | null | undefined, fallback = '/'): string {
  if (!candidate || !candidate.startsWith('/')) {
    return fallback;
  }
  // `//host` and `/\host` are protocol-relative URLs, not local paths.
  if (candidate.startsWith('//') || candidate.startsWith('/\\')) {
    return fallback;
  }
  if (candidate.includes('\n') || candidate.includes('\r')) {
    return fallback;
  }
  return candidate;
}

export function buildLoginUrl(returnPath = '/'): string {
  const target = sanitizeReturnPath(returnPath, '/');
  return `${AUTH_ROUTES.login}?post_login_redirect_uri=${encodeURIComponent(target)}`;
}

export function buildLogoutUrl(returnPath = '/login'): string {
  const target = sanitizeReturnPath(returnPath, '/login');
  return `${AUTH_ROUTES.logout}?post_logout_redirect_uri=${encodeURIComponent(target)}`;
}

/** Reads the current platform session. Returns `null` when not signed in. */
export async function fetchClientPrincipal(): Promise<SwaClientPrincipal | null> {
  const response = await fetch(AUTH_ROUTES.me, {
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) {
    return null;
  }
  const body = (await response.json().catch(() => null)) as SwaAuthMeResponse | null;
  const principal = body?.clientPrincipal ?? null;
  if (!principal || !principal.userRoles?.includes('authenticated')) {
    return null;
  }
  return principal;
}
